import { formatUkDateSafe } from "../../../Helpers/Dates";
import {
  IMPROVE_EXPORT_COLS,
  IMPROVE_TASK_COLS,
  ImprovementDetail,
  ImproveAreaExportData,
  ImproveExportColumnKey,
  ImproveExportFieldVisibility,
  ImproveExportOptions,
  ImproveTaskDetail,
  fullImproveExportFieldVisibility,
} from "./types";
import {
  assignedToNames,
  formatUrlCell,
  tagsJoin,
  taskAssigneeName,
} from "./improveExportHelpers";

function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function bodyHtml(text: string): string {
  return escapeHtml(text).replace(/\r\n/g, "\n").split("\n").join("<br>");
}

function displayCell(value: string): string {
  const t = value.trim();
  return t.length > 0 ? bodyHtml(t) : "—";
}

function formatExportTimestamp(): string {
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date());
}

function dlBlock(
  pairs: { key: ImproveExportColumnKey; value: string }[],
  vis: ImproveExportFieldVisibility
): string {
  const rows = pairs
    .filter((p) => vis[p.key])
    .map(
      (p) =>
        `<div class="dl-row"><dt>${escapeHtml(p.key)}</dt><dd>${displayCell(
          p.value ?? ""
        )}</dd></div>`
    );
  if (!rows.length) return "";
  return `<div class="dl-block">${rows.join("")}</div>`;
}

function planPairs(area: ImproveAreaExportData): { key: ImproveExportColumnKey; value: string }[] {
  return [
    { key: "Plan title", value: area.title ?? "" },
    { key: "Due date", value: formatUkDateSafe(area.endDate) },
    { key: "Owner", value: area.ownerName ?? "" },
    { key: "Plan status", value: area.status ?? "" },
  ];
}

function improvementPairs(imp: ImprovementDetail): {
  key: ImproveExportColumnKey;
  value: string;
}[] {
  return [
    { key: "Objective", value: imp.title ?? "" },
    { key: "Improvement status", value: imp.status ?? "" },
    { key: "Assigned to", value: assignedToNames(imp.assignedTo) },
    { key: "Target date", value: formatUkDateSafe(imp.targetDate) },
    { key: "Completion date", value: formatUkDateSafe(imp.completionDate) },
    { key: "Expected impact", value: imp.expectedImpact ?? "" },
    { key: "Actual impact", value: imp.actualImpact ?? "" },
    { key: "Tags", value: tagsJoin(imp.tags) },
    { key: "Custom tags", value: imp.customTags ?? "" },
    { key: "Custom date", value: formatUkDateSafe(imp.customDate) },
    { key: "Column A", value: imp.customTextA ?? "" },
    { key: "Column B", value: imp.customTextB ?? "" },
    { key: "Column C", value: imp.customTextC ?? "" },
    { key: "URL 1", value: formatUrlCell(imp.uRL1, imp.uRL1Text) },
    { key: "URL 2", value: formatUrlCell(imp.uRL2, imp.uRL2Text) },
    { key: "URL 3", value: formatUrlCell(imp.uRL3, imp.uRL3Text) },
  ];
}

function taskPairs(task: ImproveTaskDetail): {
  key: ImproveExportColumnKey;
  value: string;
}[] {
  return [
    { key: "Task heading", value: task.taskHeading ?? "" },
    { key: "Task status", value: task.taskStatus ?? "" },
    { key: "Task target date", value: formatUkDateSafe(task.targetDate) },
    {
      key: "Task completion date",
      value: formatUkDateSafe(task.completionDate),
    },
    { key: "Task assigned to", value: taskAssigneeName(task) },
    { key: "Column D", value: task.customTextD ?? "" },
    { key: "Column E", value: task.customTextE ?? "" },
    { key: "Column F", value: task.customTextF ?? "" },
    { key: "Task custom tags", value: task.customTagsB ?? "" },
  ];
}

function hasVisible(
  keys: readonly ImproveExportColumnKey[],
  vis: ImproveExportFieldVisibility
): boolean {
  return keys.some((k) => vis[k]);
}

function buildAreaHtml(area: ImproveAreaExportData, vis: ImproveExportFieldVisibility): string {
  const planDl = dlBlock(planPairs(area), vis);
  const improvements = area.improvements ?? [];
  const objectiveSections: string[] = [];

  for (const imp of improvements) {
    const impDl = dlBlock(improvementPairs(imp), vis);
    const tasks = imp.tasks?.length ? imp.tasks : [];
    const taskBlocks: string[] = [];
    if (hasVisible(IMPROVE_TASK_COLS as readonly ImproveExportColumnKey[], vis)) {
      if (tasks.length === 0) {
        taskBlocks.push(
          `<div class="task-flat"><p class="muted">No tasks</p></div>`
        );
      } else {
        for (const task of tasks) {
          const tDl = dlBlock(taskPairs(task), vis);
          if (tDl) {
            taskBlocks.push(`<div class="task-card">${tDl}</div>`);
          }
        }
      }
    }
    const objTitle = escapeHtml((imp.title ?? "Objective").trim() || "Objective");
    objectiveSections.push(
      `<section class="objective-block">
  <h3 class="objective-heading">${objTitle}</h3>
  ${impDl}
  ${taskBlocks.length ? `<div class="tasks-wrap">${taskBlocks.join("\n")}</div>` : ""}
</section>`
    );
  }

  if (improvements.length === 0) {
    objectiveSections.push(
      `<p class="muted">No objectives in this track plan.</p>`
    );
  }

  const areaTitle = escapeHtml((area.title ?? "Track plan").trim() || "Track plan");
  return `<article class="plan-card">
  <header class="plan-header">
    <h2 class="plan-title">${areaTitle}</h2>
  </header>
  ${planDl}
  <div class="objectives-wrap">${objectiveSections.join("\n")}</div>
</article>`;
}

const STYLES = `
  :root { --navy: rgb(36, 57, 79); --grey-mid: #6b7280; --grey-light: #e5e7eb; --bg: #f9fafb; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
    line-height: 1.5; color: #111827; background: var(--bg); }
  .report-page-header { background: var(--navy); color: #fff; padding: 1.25rem 1.5rem; margin-bottom: 1rem; }
  .report-page-header h1 { margin: 0 0 0.35rem 0; font-size: 1.35rem; font-weight: 700; }
  .report-page-header .sub { margin: 0; font-size: 0.9rem; opacity: 0.9; }
  .report-root { max-width: 960px; margin: 0 auto 2rem; padding: 0 1rem 2rem; }
  .plan-card { background: #fff; border: 1px solid var(--grey-light); border-radius: 8px; margin-bottom: 1.5rem;
    box-shadow: 0 1px 2px rgba(15,23,42,0.06); overflow: hidden; }
  .plan-header { background: var(--navy); color: #fff; padding: 0.85rem 1rem; }
  .plan-title { margin: 0; font-size: 1.05rem; font-weight: 600; }
  .plan-card > .dl-block { padding: 1rem 1rem 0; }
  .dl-block { margin-bottom: 1rem; }
  .dl-row { display: grid; grid-template-columns: minmax(10rem, 16rem) 1fr; gap: 0.35rem 1rem;
    margin-bottom: 0.5rem; font-size: 0.92rem; }
  .dl-row dt { color: var(--grey-mid); font-weight: 600; }
  .dl-row dd { margin: 0; }
  .objectives-wrap { padding: 0 1rem 1rem; }
  .objective-block { border: 1px solid var(--grey-light); border-radius: 6px; padding: 1rem; margin-top: 1rem;
    background: #fafafa; }
  .objective-heading { margin: 0 0 0.75rem 0; font-size: 1rem; color: var(--navy); }
  .tasks-wrap { margin-top: 0.75rem; display: flex; flex-direction: column; gap: 0.75rem; }
  .task-card { border: 1px solid #d1d5db; border-radius: 4px; padding: 0.75rem; background: #fff; }
  .task-flat .muted, .muted { color: var(--grey-mid); font-size: 0.9rem; margin: 0; }
  @media print {
    body { background: #fff; }
    .report-page-header, .plan-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
`;

function buildDocumentHtml(bodyInner: string, pageTitle: string): string {
  const safeTitle = escapeHtml(pageTitle);
  const exported = escapeHtml(formatExportTimestamp());
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${safeTitle}</title>
<style>${STYLES}</style>
</head>
<body>
<header class="report-page-header">
  <h1>${safeTitle}</h1>
  <p class="sub">Track plans export · ${exported}</p>
</header>
<div class="report-root">${bodyInner}</div>
</body>
</html>`;
}

export async function improveHtmlExporter(
  areas: ImproveAreaExportData[],
  baseFilename: string,
  options?: ImproveExportOptions
): Promise<Blob> {
  if (areas.length === 0) {
    throw new Error("No areas to export.");
  }

  const vis = fullImproveExportFieldVisibility(options?.fieldVisibility);
  if (!IMPROVE_EXPORT_COLS.some((k) => vis[k] === true)) {
    throw new Error("No export columns selected.");
  }

  const pageTitle =
    areas.length === 1
      ? (areas[0].title ?? "").trim() || baseFilename
      : `Track plans export (${areas.length})`;

  const bodyInner = areas.map((a) => buildAreaHtml(a, vis)).join("\n");
  const html = buildDocumentHtml(bodyInner, pageTitle);
  return new Blob([html], { type: "text/html;charset=utf-8" });
}
